// Regalos por elegir en «Agrega accesorios»: los marcados en el 04 sin regalo (requeridos) y las
// demás promos de regalo con varias opciones (opcionales). Puro, sin React.
import type {
    EvaluatedSalePromotion,
    EvaluateSalePromotionsResponse,
} from '../api/wizardPromotionsContract';
import { giftValueCentsOf } from './giftPanel';
import { chosenOptions, type PhoneDraft } from './phoneDraft';
import { forAnchor } from './promotionAppliesTo';
import {
    choicesOn,
    withSolo,
    withStackable,
    type PromotionChoice,
    type PromotionSelection,
} from './promotionSelection';

export interface GiftChoiceOption {
    sku: string;
    name: string;
    valueCents: number;
    inStock: boolean;
}

export interface PendingGift {
    promotionId: string;
    /** Versión evaluada; sin evaluación todavía no se puede apartar. */
    promotionVersion: number | null;
    name: string;
    stackable: boolean;
    /** Marcada en el 04 y con regalo que elegir: bloquea «Continuar» hasta elegir. */
    required: boolean;
    options: GiftChoiceOption[];
    /** Por qué ya no se puede elegir aquí; `null` si se puede. No bloquea. */
    unavailable: string | null;
}

/** La promo marcada en el 04 ya no viene en la evaluación de la venta. */
export const GIFT_GONE_COPY = 'Esta promo ya no aplica a la venta.';
/** Ninguna opción del regalo tiene existencia. */
export const GIFT_NO_STOCK_COPY = 'Ningún regalo de esta promo tiene existencia.';

/** Opciones de regalo de la promo evaluada, A-Z. */
function optionsOf(promo: EvaluatedSalePromotion | undefined): GiftChoiceOption[] {
    return (promo?.giftOptions ?? [])
        .map((o) => ({
            sku: o.sku,
            name: o.name,
            valueCents: giftValueCentsOf(o),
            inStock: o.inStock,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));
}

/**
 * Lo que falta elegir en la ancla: las marcadas en el 04 con regalo pendiente (requeridas) y las
 * demás de regalo de la evaluación con más de una opción en existencia (opcionales).
 */
export function pendingGiftsOf({
    draft,
    anchorSku,
    anchorLineId,
    selection,
    evaluation,
    withoutSureKeep,
}: {
    draft: PhoneDraft | undefined;
    anchorSku: string;
    anchorLineId: string;
    selection: PromotionSelection;
    evaluation: EvaluateSalePromotionsResponse | undefined;
    withoutSureKeep: boolean;
}): PendingGift[] {
    const held = new Set(choicesOn(selection, anchorLineId).map((c) => c.promotionId));
    const evaluated = new Map((evaluation?.promotions ?? []).map((p) => [p.promotionId, p]));
    const marked: PendingGift[] =
        draft?.sku === anchorSku ? markedGifts(draft, held, evaluated, evaluation) : [];
    if (!evaluation) return marked;
    const anchorSolo = selection.byLine[anchorLineId]?.solo ?? null;
    const markedIds = new Set(marked.map((g) => g.promotionId));
    const optional = forAnchor(evaluation.promotions, withoutSureKeep)
        .filter(
            (p) =>
                p.kind === 'GIFT' &&
                !p.blockedBy &&
                !held.has(p.promotionId) &&
                !markedIds.has(p.promotionId) &&
                p.giftOptions.filter((o) => o.inStock).length > 1 &&
                // Una que va sola no desplaza la del teléfono.
                (p.stackable || anchorSolo === null),
        )
        .map((p) => ({
            promotionId: p.promotionId,
            promotionVersion: p.version,
            name: p.name,
            stackable: p.stackable,
            required: false,
            options: optionsOf(p),
            unavailable: null,
        }));
    return [...marked, ...optional];
}

/** Las marcadas en el 04 que esperan su regalo: bloquean mientras se puedan elegir. */
function markedGifts(
    draft: PhoneDraft,
    held: ReadonlySet<string>,
    evaluated: ReadonlyMap<string, EvaluatedSalePromotion>,
    evaluation: EvaluateSalePromotionsResponse | undefined,
): PendingGift[] {
    return chosenOptions(draft)
        .filter((o) => o.giftPending && !held.has(o.promotionId))
        .map((o) => {
            const promo = evaluated.get(o.promotionId);
            const options = optionsOf(promo);
            // Sin evaluación aún no se sabe: sigue pendiente y bloquea.
            const unavailable = !evaluation
                ? null
                : !promo
                  ? GIFT_GONE_COPY
                  : options.some((opt) => opt.inStock)
                    ? null
                    : GIFT_NO_STOCK_COPY;
            return {
                promotionId: o.promotionId,
                promotionVersion: promo?.version ?? null,
                name: promo?.name ?? o.name,
                stackable: promo?.stackable ?? o.stackable,
                required: unavailable === null,
                options,
                unavailable,
            };
        });
}

/** Aparta el regalo elegido en la ancla, como la que va sola o como acumulable. */
export function withGiftChosen(
    base: PromotionSelection,
    anchorLineId: string,
    gift: Pick<PendingGift, 'promotionId' | 'promotionVersion' | 'stackable'>,
    sku: string,
): PromotionSelection {
    const anchor = base.anchorLineId ?? anchorLineId;
    const choice: PromotionChoice = {
        promotionId: gift.promotionId,
        ...(gift.promotionVersion !== null && { promotionVersion: gift.promotionVersion }),
        giftSkus: [sku],
    };
    const next = { ...base, anchorLineId: anchor };
    return gift.stackable
        ? withStackable(next, anchor, choice, true)
        : withSolo(next, anchor, choice);
}

/** Quita ese regalo de la ancla; sin regalos, la promo entera. */
export function withGiftRemoved(
    base: PromotionSelection,
    anchorLineId: string,
    promotionId: string,
    sku: string,
): PromotionSelection {
    const line = base.byLine[anchorLineId];
    if (!line) return base;
    const strip = (c: PromotionChoice): PromotionChoice | null => {
        if (c.promotionId !== promotionId) return c;
        const giftSkus = c.giftSkus.filter((s) => s !== sku);
        return giftSkus.length > 0 ? { ...c, giftSkus } : null;
    };
    const solo = line.solo ? strip(line.solo) : null;
    const stackables = line.stackables.map(strip).filter((c): c is PromotionChoice => c !== null);
    return { ...base, byLine: { ...base.byLine, [anchorLineId]: { solo, stackables } } };
}
